import { useCallback, useMemo } from 'react';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';
import { useSuspenseQuery } from '@tanstack/react-query';
import {
    createColumnHelper,
    getCoreRowModel,
    getExpandedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { Badge } from '@givebutter/primitives/badge';
import { Box } from '@givebutter/primitives/box';
import { Button } from '@givebutter/primitives/button';
import { maskWholeNumber, pluralize } from '@givebutter/util';
import {
    faChevronLeft,
    faEdit,
    faHashtag,
    faMerge,
    faSearch,
    faTrash,
} from '@fortawesome/pro-regular-svg-icons';
import { faPlus, faTags } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchTags } from 'api/tags';
import Content from 'assets/js/components/Content';
import * as DeleteTagModal from 'assets/js/pages/contacts/ContactDeleteTagModal';
import * as ContactTagCreateFormPopover from 'assets/js/pages/contacts/ContactTagCreateFormPopover';
import { ContactTagEditForm } from 'assets/js/pages/contacts/ContactTagEditForm';
import * as DropdownMenu from 'components/dropdown-menu';
import { transformSavedFilterGroups } from 'components/filter/filters-next/downstream';
import * as Input from 'components/input';
import * as Table from 'components/table';
import { useSyncPaginatedData } from 'components/table/useSyncPaginatedData';
import * as Tooltip from 'components/tooltip/Tooltip';
import { TruncatedText } from 'components/truncated-text';
import { useFiltersCache } from 'contexts/useFiltersCache';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import { usePersistentSearch } from 'customHooks/usePersistentSearch';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Tag } from 'types/domain';
import { OutboundFilters } from 'types/request';

const columnHelper = createColumnHelper<Tag>();

const ContactTags = () => {
    const { accountId } = useCurrentAccount();
    const { pagination, setPagination, sorting, setSorting } = Table.useReactTableState({
        sortingState: [{ id: 'created_at', desc: true }],
    });
    const [search, setSearch] = usePersistentSearch();
    const searchTerm = useDebounce(search, 250);
    const history = useHistory();
    const updateFilterByKey = useFiltersCache(state => state.updateFiltersNextByKey);
    const match = useRouteMatch();

    const [isPending, params] = useIsPending(
        {
            with_count: true,
            page: pagination.pageIndex + 1,
            limit: 20,
            ...(searchTerm ? { query: searchTerm } : {}),
            ...(sorting.length > 0
                ? { [`sortBy${sorting[0].desc ? 'Desc' : ''}`]: sorting[0].id }
                : {}),
        },
        500,
    );

    const { data } = useSuspenseQuery({
        queryKey: accountQueryKeys.tagsWithFilters(accountId, params),
        queryFn: () => fetchTags(accountId, params),
    });

    const handleViewTagsContactsClick = useCallback(
        (tagName: string) => {
            const values: OutboundFilters = [
                {
                    type: 'or',
                    children: {
                        tags: [
                            {
                                value: [tagName],
                                type: 'and',
                                operator: 'all',
                            },
                        ],
                    },
                },
            ];

            updateFilterByKey('contacts', transformSavedFilterGroups(values));

            history.push(`/accounts/${accountId}/contacts`, { fromTag: tagName });
        },
        [accountId, history, updateFilterByKey],
    );

    const columns = useMemo(
        () => [
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Tag',
                cell: info => (
                    <div className={'flex max-w-[230px] items-center gap-1'}>
                        <FontAwesomeIcon icon={faHashtag} className="text-gray-400" size="xs" />
                        <TruncatedText>
                            <p className="font-medium">{info.getValue()}</p>
                        </TruncatedText>
                    </div>
                ),
                meta: {
                    className: 'w-[230px]',
                },
            }),
            columnHelper.accessor('object_count', {
                id: 'object_count',
                header: '',
                enableSorting: false,
                cell: info => {
                    return (
                        <Tooltip.Root>
                            <Tooltip.Trigger asChild={true}>
                                <Button
                                    variant="none"
                                    color="primary"
                                    onClick={() =>
                                        handleViewTagsContactsClick(info.row.original.name)
                                    }
                                >
                                    <Badge color="primary">
                                        {maskWholeNumber(info.getValue() ?? '')}{' '}
                                        {pluralize(info.getValue() || 0, 'contact')}
                                    </Badge>
                                </Button>
                            </Tooltip.Trigger>
                            <Tooltip.Portal>
                                <Tooltip.Content
                                    className={
                                        '!bg-gray-900 !px-3 !py-2 text-xs font-medium !text-white'
                                    }
                                    inverse={true}
                                >
                                    View
                                    <Tooltip.Arrow inverse={true} />
                                </Tooltip.Content>
                            </Tooltip.Portal>
                        </Tooltip.Root>
                    );
                },
            }),
            columnHelper.display({
                id: Table.Constants.Actions,
                cell: info => {
                    const tag = info.row.original;

                    return (
                        <DropdownMenu.Root key={tag.id}>
                            <Table.RowDropdownTrigger />
                            <DropdownMenu.Content sideOffset={5} align={'end'}>
                                <DropdownMenu.Item
                                    onClick={() => history.push(`${match.url}/${tag.id}/merge`)}
                                    className="flex gap-2"
                                >
                                    <FontAwesomeIcon icon={faMerge} />
                                    Merge tag
                                </DropdownMenu.Item>

                                <DropdownMenu.Sub>
                                    <DropdownMenu.SubTrigger className="flex items-center gap-2 p-2 text-sm font-medium">
                                        <FontAwesomeIcon icon={faEdit} />
                                        Edit Name
                                    </DropdownMenu.SubTrigger>
                                    <DropdownMenu.Portal>
                                        <DropdownMenu.SubContent className="!p-0">
                                            <ContactTagEditForm tag={tag} />
                                        </DropdownMenu.SubContent>
                                    </DropdownMenu.Portal>
                                </DropdownMenu.Sub>

                                <DeleteTagModal.Root
                                    tag={tag}
                                    handleViewContacts={handleViewTagsContactsClick}
                                >
                                    <DeleteTagModal.Trigger asChild={true}>
                                        <DropdownMenu.Item
                                            onSelect={e => e.preventDefault()}
                                            className="flex gap-2 text-red-600"
                                        >
                                            <FontAwesomeIcon icon={faTrash} /> Delete
                                        </DropdownMenu.Item>
                                    </DeleteTagModal.Trigger>
                                </DeleteTagModal.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Root>
                    );
                },
            }),
        ],
        [handleViewTagsContactsClick, history, match.url],
    );

    const table = useReactTable({
        data: data?.data ?? [],
        columns,
        pageCount: data?.meta.last_page ?? -1,
        getCoreRowModel: getCoreRowModel(),
        getExpandedRowModel: getExpandedRowModel(),
        state: {
            pagination,
            sorting,
        },
        onPaginationChange: setPagination,
        onSortingChange: setSorting,
        manualPagination: true,
        manualSorting: true,
        enableHiding: false,
        enableColumnPinning: false,
    });

    useSyncPaginatedData(table, data);

    return (
        <div className={'flex flex-col gap-y-6'}>
            <Link className={'w-fit'} to={`/accounts/${accountId}/contacts`}>
                <Button variant="none" size="sm" className="text-gray-600">
                    <FontAwesomeIcon icon={faChevronLeft} />
                    Back to contacts
                </Button>
            </Link>

            <div className={'flex flex-col gap-4'}>
                <div className={'flex items-center gap-1.5'}>
                    <div className={'flex h-4 w-4 items-center'}>
                        <FontAwesomeIcon className={'h-full w-full text-gray-600'} icon={faTags} />
                    </div>
                    <p className={'text-2xl font-medium text-gray-900'}>Tags</p>
                </div>
                <div className="flex flex-row items-center justify-between gap-4">
                    <Input.Root className={'w-80'}>
                        <FontAwesomeIcon icon={faSearch} className={'text-gray-300'} />
                        <Input.Form
                            placeholder={'Search'}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </Input.Root>

                    <ContactTagCreateFormPopover.Root>
                        <ContactTagCreateFormPopover.Trigger asChild>
                            <Button variant="solid">
                                <FontAwesomeIcon icon={faPlus} className="text-white" />
                                New Tag
                            </Button>
                        </ContactTagCreateFormPopover.Trigger>
                    </ContactTagCreateFormPopover.Root>
                </div>

                <Table.Root isLoading={isPending} table={table} variant="bordered">
                    <div className="flex flex-col gap-4">
                        <Box className="flex flex-col overflow-hidden">
                            <div className="relative overflow-x-auto">
                                <Table.Content>
                                    <Table.Header />
                                    <Table.Body />
                                </Table.Content>
                                <Table.Empty variant={'primary'}>
                                    <Table.EmptyIcon>
                                        <FontAwesomeIcon icon={faTags} />
                                    </Table.EmptyIcon>
                                    <Table.EmptyTitle>No tags created</Table.EmptyTitle>
                                    <Table.EmptyWhenNotSearching>
                                        <Table.EmptyDescription>
                                            Create your first tag
                                        </Table.EmptyDescription>
                                        <ContactTagCreateFormPopover.Root
                                            side="bottom"
                                            align="center"
                                        >
                                            <ContactTagCreateFormPopover.Trigger asChild>
                                                <Button variant="solid" className="mt-5">
                                                    <FontAwesomeIcon
                                                        icon={faPlus}
                                                        className="text-white"
                                                    />
                                                    New Tag
                                                </Button>
                                            </ContactTagCreateFormPopover.Trigger>
                                        </ContactTagCreateFormPopover.Root>
                                    </Table.EmptyWhenNotSearching>
                                </Table.Empty>
                            </div>
                        </Box>
                        <Table.Pagination>
                            <Table.PaginationPageNavigation />
                        </Table.Pagination>
                    </div>
                </Table.Root>
            </div>
        </div>
    );
};

const ContactTagsWrapper = () => {
    return (
        <Content>
            <ContactTags />
        </Content>
    );
};

export { ContactTagsWrapper as ContactTags };
